import { TableCell } from '@talentmesh/ui/components/table';
import { cn } from '@talentmesh/ui/lib/utils';

import { constructRow } from '@tanstack/react-table';
import { type CSSProperties, useMemo } from 'react';

import { type MRT_Row, type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type TableProps, type TableTrProps } from '../../types/mrt-ui-props';
import { mergeCssVars } from '../../utils/mrt-style';
import { MRT_ExpandButton } from '../buttons/MRT_ExpandButton';
import classes from './MRT_TableBody.module.css';
import { MRT_TableBodyRow } from './MRT_TableBodyRow';

interface Props<TData extends MRT_RowData> extends TableTrProps {
    table: MRT_TableInstance<TData>;
    tableProps: Partial<TableProps>;
}

export const MRT_TableBodyEmptyRow = <TData extends MRT_RowData>({
    table,
    tableProps,
    ...commonRowProps
}: Props<TData>) => {
    const {
        getState,
        options: { layoutMode, localization, renderDetailPanel, renderEmptyRowsFallback },
        refs: { tablePaperRef },
    } = table;
    const { columnFilters, globalFilter } = getState();

    const emptyRow = useMemo(
        () => constructRow(table as any, 'mrt-row-empty', {} as TData, 0, 0) as MRT_Row<TData>,
        [],
    );

    const emptyRowProps = {
        ...commonRowProps,
        renderedRowIndex: 0,
        row: emptyRow,
        virtualRow: undefined,
    };

    return (
        <MRT_TableBodyRow
            className={cn('mrt-table-body-row', layoutMode?.startsWith('grid') && classes['empty-row-tr-grid'])}
            table={table}
            tableProps={tableProps}
            {...emptyRowProps}
        >
            {renderDetailPanel && (
                <TableCell
                    className={cn(
                        'mrt-table-body-cell',
                        layoutMode?.startsWith('grid') && classes['empty-row-td-grid'],
                    )}
                    colSpan={1}
                >
                    <MRT_ExpandButton row={emptyRow} table={table} />
                </TableCell>
            )}
            <td
                className={cn('mrt-table-body-cell', layoutMode?.startsWith('grid') && classes['empty-row-td-grid'])}
                colSpan={table.getVisibleLeafColumns().length}
            >
                {renderEmptyRowsFallback?.({ table }) ?? (
                    <span
                        className={cn(classes['empty-row-td-content'])}
                        style={
                            mergeCssVars({
                                '--mrt-paper-width': `${tablePaperRef.current?.clientWidth}`,
                            }) as CSSProperties
                        }
                    >
                        {globalFilter || columnFilters.length
                            ? localization.noResultsFound
                            : localization.noRecordsToDisplay}
                    </span>
                )}
            </td>
        </MRT_TableBodyRow>
    );
};
